"use client";
import React, { useRef, useEffect } from "react";
import ArrowRightIcon from "../../../assets/svg/arrow_right_24px.svg";
import CancelIcon from "../../../assets/svg/cancel.svg";
import Popup from "../Popup";
import Button from "../../atoms/Button";
import { Title } from "../../atoms/Title/title";
import Alert from "../../atoms/Alert";
import { AlertPopupProps } from "./types";
import "./styles.scss";

const AlertPopup: React.FC<AlertPopupProps> = ({
  onClose,
  onLoad,
  title,
  content,
  alertMessage,
  redirect = {},
}) => {
  const alertRef = useRef<HTMLButtonElement | null>(null);
  const handleKeyDown = (ev: any) => {
    if (ev.key === "Enter") {
      handleOnClose(ev);
    }
  };

  const handleOnClose = (ev: any) => {
    if (typeof onClose === "function") {
      onClose(ev);
    }
  };

  const handleRedirectOnClick = (ev: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    // Alert-specific side effects (e.g. dataLayer tracking) run before close and navigation.
    if (typeof redirect.onClick === "function") {
      redirect.onClick(ev);
    }
    // Use a full page load instead of Next.js
    // client navigation so FormAlertsPopups and shared form state do not persist across forms.
    if (redirect.to) {
      window.location.assign(redirect.to);
    }
  };

  useEffect(() => {
    alertRef?.current?.focus();
  }, []);

  return (
    <Popup
      data-testid="alert-popup"
      onLoad={onLoad}
      onClose={onClose}
      className="alertPopup"
      onClickOutside={handleOnClose}
      onKeypressEsc={handleOnClose}>
      <div className="alertPopup__header">
        <button
          ref={alertRef}
          className="alertPopup__close"
          onClick={handleOnClose}
          onKeyDown={handleKeyDown}>
          <CancelIcon />
        </button>
      </div>

      <div className="alertPopup__body">
        {title && (
          <Title
            className="alertPopup__title"
            regular
            content={title}
          />
        )}
        <div
          data-testid="content"
          className="alertPopup__content">
          {content}
        </div>
      </div>
      {redirect.label && redirect.to && (
        <div className="alertPopup__action">
          <Button
            full
            primary
            label={redirect.label}
            svg={<ArrowRightIcon />}
            onClick={handleRedirectOnClick}
            className="alertPopup__button"
          />
        </div>
      )}
      <div className="alertPopup__alert">
        <Alert
          type="warning"
          content={alertMessage}
        />
      </div>
    </Popup>
  );
};

export default AlertPopup;
